import { useEffect, useState } from 'react';
import { ALL_EVENTS } from '../../worklog/types';
import { fmtDuration } from '../../worklog/format';
import { liveDelta, nowSeconds } from '../../worklog/liveCounter';
import { useWorklog } from '../../state/WorklogContext';
import { useStatus } from '../../state/StatusContext';
import { TasksTable } from '../TasksTable';
import { WorklogSection } from '../WorklogSection';

export function OverviewPage() {
  // WorklogContext still owns the user's selected activeContract + feeds
  // the events-list audit view below. Headline numbers come from snapshot.
  const { state, refresh } = useWorklog();
  const { activeContract } = state;
  const { snapshot, refresh: refreshStatus } = useStatus();

  // 1-second tick so the live counter advances between status polls.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => forceTick(t => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Map UI's activeContract sentinel to the snapshot's contract name.
  const contractKey = activeContract === ALL_EVENTS ? 'all_events' : activeContract;
  const activeContractData = snapshot?.contracts.find(c => c.name === contractKey)
    ?? snapshot?.contracts.find(c => c.active)
    ?? null;

  const headlineS = activeContractData?.headline.contract_total_s ?? 0;
  const counts = activeContractData?.status_counts ?? { total: 0 };
  const totalItems = counts.total ?? 0;

  // Live counter delta: snapshot totals already include time through
  // snapshot.published_at; we only advance by (now - published_at).
  const delta = liveDelta(snapshot, nowSeconds());
  const totalS = headlineS + delta;

  const session = snapshot?.active_session ?? null;
  const isPaused = session?.is_paused ?? false;
  const isActive = session != null;
  const currentTaskTotalS = (session?.current_task_total_s ?? 0) + delta;

  let timeSub: string;
  if (isActive && session) {
    const verb = isPaused ? 'paused' : 'active';
    timeSub = verb + ': ' + session.task + ' (' + fmtDuration(currentTaskTotalS) + ')';
  } else {
    timeSub = 'no open session';
  }

  const contractLabel = activeContract === ALL_EVENTS
    ? 'All events'
    : activeContract;

  const worklogMeta = activeContract === ALL_EVENTS
    ? 'every event ever logged'
    : 'all events in ' + contractLabel;

  return (
    <>
      <h2>{contractLabel} status</h2>

      <div className="overview-metrics">
        <div className="om-comet om-comet-top" aria-hidden="true" />
        <div className="om-glow om-glow-top" aria-hidden="true" />

        <div className="om-time-block">
          <div className="om-time-row">
            <span className="om-time-big">{fmtDuration(totalS)}</span>
            {isActive ? (
              isPaused
                ? <span className="om-time-live om-time-paused">paused</span>
                : <span className="om-time-live">+ live</span>
            ) : null}
          </div>
          <div className="om-time-label">Time tracked</div>
          <div className="om-time-sub">{timeSub}</div>
        </div>

        <div className="om-stats">
          <div className="om-stat"><span className="om-stat-num">{counts.active ?? 0}</span><span className="om-stat-label">Active</span></div>
          <div className="om-stat"><span className="om-stat-num">{counts.open ?? 0}</span><span className="om-stat-label">Open</span></div>
          <div className="om-stat"><span className="om-stat-num">{counts.blocked ?? 0}</span><span className="om-stat-label">Blocked</span></div>
          <div className="om-stat"><span className="om-stat-num">{counts.review ?? 0}</span><span className="om-stat-label">Review</span></div>
          <div className="om-stat"><span className="om-stat-num">{counts.done ?? 0}</span><span className="om-stat-label">Done</span></div>
          <div className="om-stat"><span className="om-stat-num">{totalItems}</span><span className="om-stat-label">Total</span></div>
        </div>

        <div className="om-glow om-glow-bottom" aria-hidden="true" />
        <div className="om-comet om-comet-bottom" aria-hidden="true" />
      </div>

      {snapshot === null ? (
        <div className="om-notice">loading status...</div>
      ) : null}

      <TasksTable />

      <h3 id="worklog-section-h3">Worklog <span className="h3-meta">{worklogMeta}</span></h3>
      <WorklogSection scopeId="overview" scope={{ tags: null }} />

      <div className="action-buttons">
        <button onClick={() => { void refresh(); void refreshStatus(); }}>Refresh</button>
      </div>
    </>
  );
}
